import api from '../config/axios';

export const testCorsConnection = async () => {
  try {
    console.log('Testing CORS connection...');
    console.log('Current API URL:', api.defaults.baseURL);
    
    // Try multiple common endpoints that might exist
    const endpoints = ['/', '/api', '/admin/login', '/health'];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);
        const response = await api.get(endpoint);
        console.log(`CORS test successful with endpoint ${endpoint}:`, response.status);
        return { 
          success: true, 
          data: response.data,
          endpoint: endpoint,
          status: response.status
        };
      } catch (error) {
        console.log(`Endpoint ${endpoint} failed:`, error.response?.status || error.message);
        // If it's a 404, try the next endpoint
        if (error.response?.status === 404) {
          continue;
        }
        // If it's a 401, that means the endpoint exists but needs auth - this is good!
        if (error.response?.status === 401) {
          console.log(`Endpoint ${endpoint} exists but requires authentication`);
          return { 
            success: true, 
            message: 'Connection successful - endpoint requires authentication',
            endpoint: endpoint,
            status: error.response.status
          };
        }
        // For other errors, throw to be caught by outer catch
        throw error;
      }
    }
    
    // If we get here, all endpoints returned 404
    console.log('All tested endpoints returned 404 - server is reachable but endpoints may not exist');
    return { 
      success: true, 
      message: 'Server is reachable but tested endpoints do not exist (404)',
      details: 'This is normal if your backend uses different endpoint paths'
    };
    
  } catch (error) {
    console.error('CORS test failed:', error);
    return { 
      success: false, 
      error: error.message,
      details: {
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      }
    };
  }
};

export const testLoginEndpoint = async (email, password) => {
  try {
    console.log('Testing login endpoint...');
    const response = await api.post('/admin/login', { email, password });
    console.log('Login test successful:', response.status);
    return { success: true, status: response.status };
  } catch (error) {
    console.error('Login test failed:', error);
    return { 
      success: false, 
      error: error.message,
      status: error.response?.status,
      details: {
        code: error.code,
        statusText: error.response?.statusText,
        url: error.config?.url
      }
    };
  }
};

export const testModelsEndpoint = async () => {
  try {
    console.log('Testing models endpoint...');
    const response = await api.get('/models');
    console.log('Models endpoint test successful:', response.data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Models endpoint test failed:', error);
    
    // If it's a 404, the endpoint doesn't exist
    if (error.response?.status === 404) {
      return { success: false, error: 'Models endpoint not found (404) - check if your backend has this endpoint' };
    }
    
    return { success: false, error: error.message };
  }
};

export const testConnection = async () => {
  try {
    console.log('Testing basic connection to API server...');
    
    // Try a simple GET request to the root
    try {
      const response = await api.get('/');
      console.log('Connection test successful with root endpoint');
      return { success: true, message: 'Connection established successfully', status: response.status };
    } catch (rootError) {
      console.log('Root endpoint failed, trying /api...');
      
      // Try /api endpoint
      try {
        const response = await api.get('/api');
        console.log('Connection test successful with /api endpoint');
        return { success: true, message: 'Connection established successfully', status: response.status };
      } catch (apiError) {
        console.log('API endpoint failed, trying OPTIONS...');
        
        // Try OPTIONS request to test CORS preflight
        try {
          await api.options('/admin/login');
          console.log('Connection test successful with OPTIONS');
          return { success: true, message: 'Connection established successfully (OPTIONS)' };
        } catch (optionsError) {
          // If all methods fail, check if it's a 404 (server reachable) vs network error
          if (optionsError.response?.status === 404) {
            return { success: true, message: 'Server is reachable but endpoints return 404' };
          }
          throw optionsError;
        }
      }
    }
  } catch (error) {
    console.error('Connection test failed:', error);
    
    // Check if it's a network error (server not running)
    if (error.code === 'ERR_NETWORK') {
      return { 
        success: false, 
        error: 'Cannot connect to server - make sure your backend is running on localhost:5000',
        details: 'Network error suggests the server is not running or not accessible'
      };
    }
    
    return { 
      success: false, 
      error: error.message,
      details: {
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText
      }
    };
  }
}; 